import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

export interface MentionableItem {
  id: string;
  displayName: string;
  /** Optional secondary line shown in the autocomplete (e.g. role, email). */
  subtitle?: string;
}

export interface MentionInputValue {
  /**
   * Body text with mention markers in the form `@[Display Name](id)`.
   * Plain text outside markers is preserved verbatim. Use
   * {@link parseMentionBody} downstream to render or strip the markers.
   */
  body: string;
  /**
   * Canonical list of mentioned ids (deduped, in mention order).
   * Backend should treat this as the source of truth — the body string is
   * for display only and doesn't need to be re-parsed by callers.
   */
  mentionedIds: string[];
}

interface MentionInputProps {
  value: MentionInputValue;
  onChange: (next: MentionInputValue) => void;
  /**
   * Datasource for the autocomplete dropdown. Called with the substring
   * the user has typed after `@` (may be empty string immediately after
   * the `@`). Implementations can ignore the query and return a static
   * list, or do server-side filtering.
   */
  fetchMentionables: (query: string) => Promise<MentionableItem[]> | MentionableItem[];
  placeholder?: string;
  disabled?: boolean;
  /** Visible character cap; ignored when undefined. */
  maxLength?: number;
  /** Hint label shown under the textarea, e.g. char count + safety note. */
  helperText?: React.ReactNode;
  className?: string;
  rows?: number;
  ariaLabel?: string;
  /** Fires on Cmd/Ctrl+Enter — convenience submit hook. */
  onSubmit?: () => void;
}

/**
 * Renders a textarea with `@`-triggered autocomplete. Maintains the canonical
 * `mentionedIds` array as the user types — callers don't need to re-parse the
 * body. Designed to be domain-agnostic: pass any datasource that produces
 * `MentionableItem[]`.
 *
 *   <MentionInput
 *     value={value}
 *     onChange={setValue}
 *     fetchMentionables={(q) => orgUsers.filter(u => u.displayName.includes(q))}
 *   />
 *
 * Returned `body` keeps mention markers in `@[Name](id)` form. Use
 * `parseMentionBody` to render plain text + mentions in a list view.
 */
export function MentionInput({
  value,
  onChange,
  fetchMentionables,
  placeholder,
  disabled,
  maxLength,
  helperText,
  className,
  rows = 4,
  ariaLabel,
  onSubmit,
}: MentionInputProps): React.ReactElement {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [openAt, setOpenAt] = useState<number | null>(null);
  const [query, setQuery] = useState<string>('');
  const [items, setItems] = useState<MentionableItem[]>([]);
  const [highlightIndex, setHighlightIndex] = useState(0);
  // Pixel offset of the `@` character within the textarea — anchors the
  // suggestion popover so it sits next to the trigger, the way Slack /
  // GitHub do it, instead of pinned to the bottom-left of the field.
  const [anchor, setAnchor] = useState<{ top: number; left: number; lineHeight: number } | null>(
    null
  );

  // Re-fetch suggestions whenever the query changes
  useEffect(() => {
    if (openAt === null) return;
    let cancelled = false;
    void Promise.resolve(fetchMentionables(query)).then((next) => {
      if (cancelled) return;
      setItems(next);
      setHighlightIndex(0);
    });
    return () => {
      cancelled = true;
    };
  }, [openAt, query, fetchMentionables]);

  // Recompute the popover anchor any time the trigger position, content,
  // or layout changes. We re-measure on resize too — a window resize can
  // reflow the textarea (different wrapping → new line for the `@`).
  useEffect(() => {
    if (openAt === null || !textareaRef.current) {
      setAnchor(null);
      return;
    }
    const measure = (): void => {
      const ta = textareaRef.current;
      if (!ta) return;
      setAnchor(getCaretCoordinates(ta, openAt));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [openAt, value.body]);

  const closeMenu = useCallback(() => {
    setOpenAt(null);
    setQuery('');
    setItems([]);
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
      const nextBody = e.target.value;
      const cursor = e.target.selectionStart;
      const mentionedIds = recomputeMentionedIds(nextBody);
      onChange({ body: nextBody, mentionedIds });

      // Detect @-trigger: most recent `@` before the cursor with no whitespace
      // between it and the cursor opens the menu.
      const upto = nextBody.slice(0, cursor);
      const atIndex = upto.lastIndexOf('@');
      if (atIndex === -1) {
        closeMenu();
        return;
      }
      const since = upto.slice(atIndex + 1);
      const isPriorBoundary = atIndex === 0 || /\s/.test(nextBody[atIndex - 1] ?? '');
      const queryCandidate = since;
      const triggerStillActive = isPriorBoundary && !/\s/.test(queryCandidate);
      if (triggerStillActive) {
        setOpenAt(atIndex);
        setQuery(queryCandidate);
      } else {
        closeMenu();
      }
    },
    [onChange, closeMenu]
  );

  const insertMention = useCallback(
    (item: MentionableItem): void => {
      if (openAt === null) return;
      const ta = textareaRef.current;
      if (!ta) return;
      const cursor = ta.selectionStart;
      const before = value.body.slice(0, openAt);
      const after = value.body.slice(cursor);
      const marker = `@[${item.displayName}](${item.id})`;
      const nextBody = `${before}${marker} ${after}`;
      const nextMentionedIds = recomputeMentionedIds(nextBody);
      onChange({ body: nextBody, mentionedIds: nextMentionedIds });

      // Restore caret position after the inserted marker + trailing space
      const nextCursor = before.length + marker.length + 1;
      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(nextCursor, nextCursor);
      });
      closeMenu();
    },
    [openAt, value.body, onChange, closeMenu]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
      if (openAt !== null && items.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setHighlightIndex((i) => (i + 1) % items.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setHighlightIndex((i) => (i - 1 + items.length) % items.length);
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          insertMention(items[highlightIndex]!);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          closeMenu();
          return;
        }
      }
      if (onSubmit && (e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        onSubmit();
      }
    },
    [openAt, items, highlightIndex, insertMention, closeMenu, onSubmit]
  );

  const charCount = value.body.length;

  return (
    <div className={cn('relative', className)}>
      <textarea
        ref={textareaRef}
        rows={rows}
        value={value.body}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(closeMenu, 100)}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-label={ariaLabel}
        className={cn(
          'w-full resize-y rounded-md border bg-background px-3 py-2 text-sm shadow-xs',
          'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:cursor-not-allowed disabled:opacity-50'
        )}
      />
      {(helperText || maxLength) && (
        <div className="mt-1 flex justify-between text-xs text-muted-foreground">
          <span>{helperText}</span>
          {maxLength && (
            <span aria-live="polite">
              {charCount} / {maxLength}
            </span>
          )}
        </div>
      )}
      {openAt !== null && items.length > 0 && anchor && (
        <div
          role="listbox"
          aria-label="Mention suggestions"
          // Anchor the popover to the `@` glyph, not the textarea bounds.
          // `top` advances one line so the list sits below the current row;
          // `min-w-48` keeps it readable when names are short.
          style={{
            top: anchor.top + anchor.lineHeight + 4,
            left: anchor.left,
          }}
          className="absolute z-50 max-h-56 min-w-48 max-w-xs overflow-y-auto rounded-md border bg-popover shadow-md"
        >
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              role="option"
              aria-selected={i === highlightIndex}
              onMouseDown={(e) => {
                // Prevent textarea blur from closing the menu before click
                e.preventDefault();
                insertMention(item);
              }}
              onMouseEnter={() => setHighlightIndex(i)}
              className={cn(
                'flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm',
                i === highlightIndex && 'bg-accent text-accent-foreground'
              )}
            >
              <span className="font-medium">{item.displayName}</span>
              {item.subtitle && (
                <span className="text-xs text-muted-foreground">{item.subtitle}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Pixel coordinates of an arbitrary character in a textarea. Same trick
 * the well-known `textarea-caret-position` library uses: render a hidden
 * <div> that mirrors the textarea's font / box / wrapping, drop the text
 * up to `position` into it, append a marker <span>, and read the span's
 * offset. We only need this on `@` triggers (low-frequency, low-volume),
 * so the GC churn is acceptable.
 *
 * Properties copied are the ones that affect text layout — anything that
 * changes glyph metrics or line wrapping has to match the textarea, or
 * the marker drifts. `scrollTop`/`scrollLeft` are subtracted so the
 * coordinates are relative to the textarea's visible viewport, which is
 * what an absolutely-positioned popover needs.
 */
function getCaretCoordinates(
  textarea: HTMLTextAreaElement,
  position: number
): { top: number; left: number; lineHeight: number } {
  const computed = window.getComputedStyle(textarea);
  const mirror = document.createElement('div');
  const props = [
    'boxSizing',
    'width',
    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth',
    'borderStyle',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft',
    'fontStyle',
    'fontVariant',
    'fontWeight',
    'fontStretch',
    'fontSize',
    'fontSizeAdjust',
    'lineHeight',
    'fontFamily',
    'textAlign',
    'textTransform',
    'textIndent',
    'letterSpacing',
    'wordSpacing',
    'tabSize',
    'MozTabSize',
  ] as const;
  for (const prop of props) {
    // @ts-expect-error — index access on CSSStyleDeclaration
    mirror.style[prop] = computed[prop];
  }
  mirror.style.position = 'absolute';
  mirror.style.visibility = 'hidden';
  mirror.style.whiteSpace = 'pre-wrap';
  mirror.style.wordWrap = 'break-word';
  mirror.style.overflow = 'hidden';
  mirror.style.top = '0';
  mirror.style.left = '-9999px';

  document.body.appendChild(mirror);
  try {
    mirror.textContent = textarea.value.slice(0, position);
    const marker = document.createElement('span');
    // Non-empty so the span has measurable height even at end-of-string.
    marker.textContent = textarea.value.slice(position) || '.';
    mirror.appendChild(marker);

    const lineHeight = parseFloat(computed.lineHeight) || parseFloat(computed.fontSize) * 1.2;
    return {
      top: marker.offsetTop - textarea.scrollTop,
      left: marker.offsetLeft - textarea.scrollLeft,
      lineHeight,
    };
  } finally {
    document.body.removeChild(mirror);
  }
}

const MENTION_RE = /@\[([^\]]+)\]\(([0-9a-fA-F-]{36})\)/g;

/**
 * Walk the body and pull the `@[Name](id)` markers, deduping by id.
 */
function recomputeMentionedIds(body: string): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const match of body.matchAll(MENTION_RE)) {
    const id = match[2]!;
    if (!seen.has(id)) {
      seen.add(id);
      ids.push(id);
    }
  }
  return ids;
}

export interface MentionBodyChunk {
  type: 'text' | 'mention';
  /** For `text`: the literal text. For `mention`: the display name. */
  text: string;
  /** For `mention`: the mentioned id. */
  id?: string;
}

/**
 * Split a body string into ordered text + mention chunks. Useful for
 * rendering — wrap each `mention` chunk in a <strong> / styled span / link
 * to the user's profile.
 */
export function parseMentionBody(body: string): MentionBodyChunk[] {
  const chunks: MentionBodyChunk[] = [];
  let cursor = 0;
  for (const match of body.matchAll(MENTION_RE)) {
    const start = match.index ?? 0;
    if (start > cursor) {
      chunks.push({ type: 'text', text: body.slice(cursor, start) });
    }
    chunks.push({ type: 'mention', text: match[1]!, id: match[2]! });
    cursor = start + match[0].length;
  }
  if (cursor < body.length) {
    chunks.push({ type: 'text', text: body.slice(cursor) });
  }
  return chunks;
}
